<template>
  <div class="medium-10 columns setup-paths">
    <warning-callout
      :title="$t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.NOTICE.TITLE')"
    >
      <p>{{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.NOTICE.QUESTION') }}</p>
      <ul class="setup-paths__notice-list">
        <li>
          <fluent-icon icon="checkmark" size="14" class="setup-path__check" />
          <span
            v-dompurify-html="
              $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.NOTICE.YES')
            "
          />
        </li>
        <li>
          <fluent-icon icon="info" size="14" class="setup-path__info" />
          <span
            v-dompurify-html="
              $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.NOTICE.NO')
            "
          />
        </li>
      </ul>
    </warning-callout>

    <div class="row setup-paths__cards">
      <!-- La tarjeta es solo contenedor; el único control interactivo es su botón. -->
      <div class="medium-6 columns setup-path">
        <span class="setup-path__badge">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.RECOMMENDED') }}
        </span>
        <span class="setup-path__icon-box">
          <fluent-icon icon="brand-facebook" size="24" />
        </span>
        <h4 class="setup-path__title">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.EMBEDDED.TITLE') }}
        </h4>
        <p class="setup-path__desc">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.EMBEDDED.DESC') }}
        </p>
        <ul class="setup-path__points">
          <li v-for="point in embeddedPoints" :key="point">
            <fluent-icon icon="checkmark" size="14" class="setup-path__check" />
            <span>{{ point }}</span>
          </li>
        </ul>
        <woot-button
          class="setup-path__action"
          is-expanded
          @click="select(EMBEDDED_PATH)"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.EMBEDDED.BUTTON') }}
        </woot-button>
      </div>

      <div class="medium-6 columns setup-path">
        <span class="setup-path__icon-box setup-path__icon-box--neutral">
          <fluent-icon icon="settings" size="24" />
        </span>
        <h4 class="setup-path__title">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.MANUAL.TITLE') }}
        </h4>
        <p class="setup-path__desc">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.MANUAL.DESC') }}
        </p>
        <ul class="setup-path__points">
          <li v-for="point in manualPoints" :key="point">
            <fluent-icon icon="info" size="14" class="setup-path__info" />
            <span>{{ point }}</span>
          </li>
        </ul>
        <woot-button
          class="setup-path__action"
          variant="hollow"
          color-scheme="secondary"
          is-expanded
          @click="select(MANUAL_PATH)"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.MANUAL.BUTTON') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import WarningCallout from 'dashboard/components/ui/WarningCallout';
import { EMBEDDED_PATH, MANUAL_PATH } from '../../helpers/whatsappConnectGuard';

// Cuántas ventajas lista cada tarjeta (claves POINT_n en i18n).
const EMBEDDED_POINTS = 3;
const MANUAL_POINTS = 2;

// Paso 1 del alta de WhatsApp Cloud con Embedded Signup: aviso de coexistencia y dos tarjetas
// (conexión asistida con Facebook o configuración manual). Emite `select` con el camino elegido.
export default {
  components: {
    WarningCallout,
  },
  data() {
    return { EMBEDDED_PATH, MANUAL_PATH };
  },
  computed: {
    // Ventajas de cada camino, para que el usuario decida sin conocer la terminología de Meta.
    embeddedPoints() {
      return this.points('EMBEDDED', EMBEDDED_POINTS);
    },
    manualPoints() {
      return this.points('MANUAL', MANUAL_POINTS);
    },
  },
  methods: {
    // Textos POINT_1..POINT_n de la tarjeta indicada.
    points(card, count) {
      return Array.from({ length: count }, (_, index) =>
        this.$t(
          `INBOX_MGMT.ADD.WHATSAPP.SETUP_PATHS.${card}.POINT_${index + 1}`
        )
      );
    },
    select(path) {
      this.$emit('select', path);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';

.setup-paths {
  margin-top: var(--space-small);

  &__notice-list {
    list-style: none;
    margin: 0;

    li {
      align-items: flex-start;
      display: flex;
      gap: var(--space-smaller);
      margin-bottom: var(--space-smaller);
    }
  }
}

// Tarjeta con el mismo lenguaje visual que las tarjetas de canal del paso anterior; el botón
// del pie es el único control (un role="button" anidado se leería dos veces con lector de pantalla).
.setup-path {
  background: var(--white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-normal);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--space-normal);
  position: relative;
  text-align: left;
  transition: all 0.2s ease-in;

  &:hover {
    border-color: var(--w-500);
    box-shadow: var(--shadow-medium);
  }

  &__badge {
    background: $conversa2-blue-700-color;
    border-radius: var(--border-radius-normal);
    color: var(--white);
    font-size: var(--font-size-micro);
    font-weight: var(--font-weight-medium);
    padding: 2px var(--space-small);
    position: absolute;
    right: var(--space-normal);
    top: var(--space-normal);
    text-transform: uppercase;
  }

  &__icon-box {
    align-items: center;
    background: $conversa2-blue-50-color;
    border-radius: var(--border-radius-normal);
    color: $conversa2-blue-700-color;
    display: inline-flex;
    height: 4rem;
    justify-content: center;
    margin-bottom: var(--space-small);
    width: 4rem;

    &--neutral {
      background: var(--s-50);
      color: var(--s-700);
    }
  }

  &__title {
    color: var(--conversa2-neutral-800-color);
    font-size: var(--font-size-default);
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-smaller);
  }

  &__desc {
    color: var(--b-600);
    font-size: var(--font-size-small);
    margin-bottom: var(--space-small);
  }

  &__points {
    list-style: none;
    margin: 0 0 var(--space-normal);
    padding: 0;

    li {
      align-items: flex-start;
      color: var(--b-700);
      display: flex;
      font-size: var(--font-size-mini);
      gap: var(--space-smaller);
      margin-bottom: var(--space-smaller);
    }
  }

  &__check {
    color: var(--g-600);
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__info {
    color: var(--s-600);
    flex-shrink: 0;
    margin-top: 2px;
  }

  // El botón se pega al pie para que ambas tarjetas alineen aunque tengan distinto texto.
  &__action {
    margin-top: auto;
  }
}
</style>
